<template>
  <v-container class="d-flex align-center justify-center" style="min-height: 80vh;">
    <v-col cols="12" sm="8" md="6" lg="4">

      <!-- Login Card -->
      <v-card v-if="showLoginUser" elevation="2" rounded="lg" class="pa-2">
        <v-card-title class="text-h5 text-center pt-4">Login</v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-text-field
              v-model="username"
              label="Username"
              prepend-inner-icon="mdi-account"
              variant="outlined"
              density="compact"
              required
              :rules="inputRulesUsername"
              class="mb-2"
            />
            <v-text-field
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              label="Password"
              prepend-inner-icon="mdi-lock"
              :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append-inner="togglePasswordVisibility"
              variant="outlined"
              density="compact"
              required
              :rules="inputRulesPassword"
              @keyup.enter="login"
            />
            <v-alert
              v-if="connectionError"
              type="warning"
              density="compact"
              class="mb-3"
              closable
              @click:close="connectionError = false"
            >
              Server-ul nu este accesibil. Verificati conexiunea la internet.
            </v-alert>
            <v-alert
              v-if="error"
              type="error"
              density="compact"
              class="mb-3"
              closable
              @click:close="error = false"
            >
              Invalid username or password.
            </v-alert>
            <v-checkbox
              v-model="agreeToTermsLogin"
              density="compact"
              hide-details
              class="mb-2"
            >
              <template #label>
                <span class="text-body-2">
                  Sunt de acord cu
                  <a href="/TermsAndConditions.pdf" target="_blank" @click.stop>Termenii si Conditiile</a>
                </span>
              </template>
            </v-checkbox>
            <v-btn
              :disabled="!valid"
              color="primary"
              variant="flat"
              block
              size="large"
              :loading="loading"
              @click="login"
            >
              Login
            </v-btn>
          </v-form>

          <div class="text-center mt-3">
            <v-btn
              variant="text"
              color="primary"
              size="small"
              @click="showResetPasswordDialog = true"
            >
              Am uitat parola
            </v-btn>
          </div>
        </v-card-text>

        <v-divider class="mx-4" />

        <v-card-actions class="justify-center pa-4">
          <v-btn
            color="secondary"
            variant="text"
            @click="setShowRegisterNewUser()"
          >
            Register new user
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- Register Step 1: Email -->
      <v-card v-if="showRegisterNewUserLevel1 && regStep === 1" elevation="2" rounded="lg" class="pa-2">
        <v-card-title class="text-h5 text-center pt-4">Register - Pasul 1</v-card-title>
        <v-card-text>
          <v-form ref="formRegisterEmail" v-model="valid" lazy-validation>
            <v-alert type="info" variant="tonal" density="compact" class="mb-4">
              Introdu adresa ta de email pentru a crea un cont nou.
            </v-alert>
            <v-text-field
              v-model="email"
              label="Email"
              prepend-inner-icon="mdi-email"
              variant="outlined"
              density="compact"
              required
              :rules="inputRulesEmail"
              :loading="checkingEmail"
              @click="cancelError"
              @keyup.enter="goToRegStep2"
              class="mb-2"
            />
            <v-alert
              v-if="emailExistsWarning"
              type="warning"
              variant="tonal"
              density="compact"
              class="mb-2"
            >
              <div>Exista deja conturi cu acest email.</div>
              <div class="mt-2 d-flex flex-wrap ga-2">
                <v-btn size="small" variant="tonal" color="primary" @click="setShowLoginUser()">
                  Autentifica-te
                </v-btn>
                <v-btn size="small" variant="tonal" color="primary" @click="showResetPasswordDialog = true">
                  Recupereaza parola
                </v-btn>
                <v-btn size="small" variant="tonal" color="secondary" :loading="sendingUsernameReminder" @click="sendUsernameReminderFromReg">
                  Trimite username-urile pe email
                </v-btn>
              </div>
              <v-alert
                v-if="usernameReminderSent"
                type="success"
                variant="tonal"
                density="compact"
                class="mt-2"
              >
                {{ usernameReminderSent }}
              </v-alert>
            </v-alert>
            <v-alert
              v-if="error"
              type="error"
              density="compact"
              class="mt-3 mb-3"
              closable
              @click:close="error = false"
            >
              {{ error }}
            </v-alert>
            <v-btn
              :disabled="!valid || !email || !/.+@.+\..+/.test(email)"
              color="primary"
              variant="flat"
              block
              size="large"
              :loading="checkingEmail"
              @click="goToRegStep2"
              class="mt-3"
            >
              {{ emailExistsWarning ? 'Continua cu cont nou' : 'Continua' }}
            </v-btn>
          </v-form>
        </v-card-text>

        <v-divider class="mx-4" />

        <v-card-actions class="justify-center pa-4">
          <v-btn
            color="primary"
            variant="text"
            @click="setShowLoginUser()"
          >
            Login existing user
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- Register Step 2: Username + Password -->
      <v-card v-if="showRegisterNewUserLevel1 && regStep === 2" elevation="2" rounded="lg" class="pa-2">
        <v-card-title class="text-h5 text-center pt-4">Register - Pasul 2</v-card-title>
        <v-card-text>
          <v-form ref="formRegister" v-model="valid" lazy-validation>
            <v-text-field
              v-model="email"
              label="Email"
              prepend-inner-icon="mdi-email"
              variant="outlined"
              density="compact"
              disabled
              class="mb-2"
            />
            <v-text-field
              v-model="regUsername"
              label="Username"
              prepend-inner-icon="mdi-account"
              variant="outlined"
              density="compact"
              required
              :rules="inputRulesUsername"
              :loading="checkingUsername"
              :messages="usernameAvailabilityMessage"
              :color="usernameAvailable === true ? 'success' : undefined"
              :error="usernameAvailable === false"
              @click="cancelError"
              class="mb-2"
            />
            <v-text-field
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              label="Password"
              prepend-inner-icon="mdi-lock"
              :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append-inner="togglePasswordVisibility"
              @click="cancelError"
              variant="outlined"
              density="compact"
              required
              :rules="inputRulesPassword"
              class="mb-2"
            />
            <v-text-field
              v-model="referral_code"
              label="Cod Referral (optional)"
              prepend-inner-icon="mdi-account-multiple-plus"
              variant="outlined"
              density="compact"
              hint="Daca ai un cod de la cineva, introdu-l aici"
              persistent-hint
              @click="cancelError"
            />
            <v-alert
              v-if="error"
              type="error"
              density="compact"
              class="mt-3 mb-3"
              closable
              @click:close="error = false"
            >
              {{ error }}
            </v-alert>
            <AntibotVerification ref="antibot" v-model:valid="antibotValid" />
            <v-checkbox
              v-model="agreeToTermsRegister"
              density="compact"
              hide-details
              class="mt-2"
              :rules="[v => !!v || 'Trebuie sa accepti termenii']"
            >
              <template #label>
                <span class="text-body-2">
                  Sunt de acord cu
                  <a href="/TermsAndConditions.pdf" target="_blank" @click.stop>Termenii si Conditiile</a>
                </span>
              </template>
            </v-checkbox>
            <v-btn
              :disabled="!valid || usernameAvailable === false || !regUsername || regUsername.length < 5 || !agreeToTermsRegister || !antibotValid"
              color="primary"
              variant="flat"
              block
              size="large"
              :loading="loading"
              @click="registerNewUser"
              class="mt-3"
            >
              Trimite cod pe email
            </v-btn>
          </v-form>
        </v-card-text>

        <v-divider class="mx-4" />

        <v-card-actions class="justify-center pa-4">
          <v-btn
            color="secondary"
            variant="text"
            @click="regStep = 1"
          >
            Inapoi
          </v-btn>
          <v-btn
            color="primary"
            variant="text"
            @click="setShowLoginUser()"
          >
            Login existing user
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- Register Level 2 Card -->
      <v-card v-if="showRegisterNewUserLevel2" elevation="2" rounded="lg" class="pa-2">
        <v-card-title class="text-h5 text-center pt-4">Confirmare Inregistrare</v-card-title>
        <v-card-text>
          <v-form ref="formRegister" v-model="valid" lazy-validation>
            <v-text-field
              v-model="email"
              disabled
              label="Email"
              prepend-inner-icon="mdi-email"
              variant="outlined"
              density="compact"
              class="mb-2"
            />
            <v-text-field
              v-model="regUsername"
              disabled
              label="Username"
              prepend-inner-icon="mdi-account"
              variant="outlined"
              density="compact"
              class="mb-2"
            />
            <v-text-field
              v-model="registration_confirmation_code"
              type="number"
              label="Cod de confirmare"
              prepend-inner-icon="mdi-shield-key"
              variant="outlined"
              density="compact"
              required
              :rules="inputRulesConfirmationCode"
              @click="cancelError"
            />
            <v-alert
              v-if="error"
              type="error"
              density="compact"
              class="mt-3 mb-3"
              closable
              @click:close="error = false"
            >
              Cod de confirmare invalid.
            </v-alert>
            <v-btn
              color="primary"
              variant="flat"
              block
              size="large"
              :loading="loading"
              @click="checkConfirmationCode"
              class="mt-3"
            >
              Confirma codul
            </v-btn>
          </v-form>
        </v-card-text>
      </v-card>

    </v-col>

    <!-- Reset Password Dialog -->
    <v-dialog v-model="showResetPasswordDialog" max-width="450px" persistent>
      <v-card rounded="lg">
        <v-card-title class="d-flex align-center justify-space-between">
          <span>Resetare Parola</span>
          <v-btn icon variant="text" size="small" @click="closeResetDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <!-- Step 1: Send code -->
          <div v-if="resetStep === 1">
            <!-- Forgot username sub-flow -->
            <div v-if="showForgotUsername">
              <v-alert type="info" variant="tonal" density="compact" class="mb-4">
                Introdu adresa de email. Vei primi un mesaj cu toate username-urile asociate.
              </v-alert>
              <v-text-field
                v-model="forgotEmail"
                label="Email"
                prepend-inner-icon="mdi-email"
                variant="outlined"
                density="compact"
                :rules="inputRulesEmail"
                @keyup.enter="sendForgotUsername"
              />
              <v-alert
                v-if="resetError"
                type="error"
                density="compact"
                class="mt-2 mb-2"
              >
                {{ resetError }}
              </v-alert>
              <v-alert
                v-if="resetSuccess"
                type="success"
                density="compact"
                class="mt-2 mb-2"
              >
                {{ resetSuccess }}
              </v-alert>
              <v-btn
                color="primary"
                variant="flat"
                block
                :loading="resetLoading"
                :disabled="!forgotEmail || !/.+@.+\..+/.test(forgotEmail)"
                @click="sendForgotUsername"
                class="mt-2"
              >
                <v-icon start>mdi-email-fast</v-icon>
                Trimite Username pe Email
              </v-btn>
              <v-btn
                variant="text"
                color="primary"
                size="small"
                block
                class="mt-2"
                @click="showForgotUsername = false; resetError = null; resetSuccess = null;"
              >
                Inapoi la resetare parola
              </v-btn>
            </div>

            <!-- Normal username entry for reset code -->
            <div v-else>
              <v-alert type="info" variant="tonal" density="compact" class="mb-4">
                Introdu username-ul contului. Vei primi un cod de resetare pe email.
              </v-alert>
              <v-text-field
                v-model="resetUsername"
                label="Username"
                prepend-inner-icon="mdi-account"
                variant="outlined"
                density="compact"
                :rules="inputRulesUsername"
                @keyup.enter="sendResetCode"
              />
              <v-alert
                v-if="resetError"
                type="error"
                density="compact"
                class="mt-2 mb-2"
              >
                {{ resetError }}
              </v-alert>
              <v-alert
                v-if="resetSuccess"
                type="success"
                density="compact"
                class="mt-2 mb-2"
              >
                {{ resetSuccess }}
              </v-alert>
              <v-btn
                color="primary"
                variant="flat"
                block
                :loading="resetLoading"
                :disabled="!resetUsername || resetUsername.length < 5"
                @click="sendResetCode"
                class="mt-2"
              >
                <v-icon start>mdi-email-fast</v-icon>
                Trimite Cod Resetare
              </v-btn>
              <v-btn
                variant="text"
                color="secondary"
                size="small"
                block
                class="mt-2"
                @click="showForgotUsername = true; resetError = null; resetSuccess = null;"
              >
                Am uitat si username-ul
              </v-btn>
            </div>
          </div>

          <!-- Step 2: Enter code + new password -->
          <div v-if="resetStep === 2">
            <v-text-field
              v-model="resetUsername"
              label="Username"
              prepend-inner-icon="mdi-account"
              variant="outlined"
              density="compact"
              disabled
              class="mb-2"
            />

            <!-- Code entry (hidden when retries exceeded) -->
            <div v-if="!retriesExceeded">
              <div class="text-subtitle-2 mb-2">Introdu codul primit pe email:</div>
              <v-otp-input
                v-model="resetCode"
                length="4"
                type="number"
                :disabled="resetLoading"
                class="mb-3"
              />

              <v-text-field
                v-model="newPassword"
                :type="showNewPassword ? 'text' : 'password'"
                label="Parola noua"
                prepend-inner-icon="mdi-lock-reset"
                :append-inner-icon="showNewPassword ? 'mdi-eye-off' : 'mdi-eye'"
                @click:append-inner="showNewPassword = !showNewPassword"
                variant="outlined"
                density="compact"
                :rules="newPasswordRules"
                hint="Minim 6 caractere, doar litere (a-z, A-Z) si cifre (0-9)"
                persistent-hint
              />

              <v-alert
                v-if="resetError"
                type="error"
                density="compact"
                class="mt-3"
              >
                {{ resetError }}
              </v-alert>

              <v-btn
                color="primary"
                variant="flat"
                block
                :loading="resetLoading"
                :disabled="resetCode.length !== 4 || !newPassword || !isValidNewPassword"
                @click="submitResetPassword"
                class="mt-3"
              >
                Reseteaza Parola
              </v-btn>
            </div>

            <!-- Retries exceeded -->
            <div v-else>
              <v-alert type="warning" density="compact" class="mb-4">
                Ai depasit numarul de incercari. Retrimite codul pe email.
              </v-alert>
              <v-btn
                color="warning"
                variant="flat"
                block
                :loading="resetLoading"
                @click="resendResetCode"
              >
                <v-icon start>mdi-email-fast</v-icon>
                Retrimite Cod
              </v-btn>
            </div>
          </div>

          <!-- Step 3: Success -->
          <div v-if="resetStep === 3">
            <v-alert type="success" variant="tonal" class="mb-4">
              <v-icon start>mdi-check-circle</v-icon>
              Parola a fost schimbata cu succes! Te poti autentifica cu noua parola.
            </v-alert>
            <v-btn
              color="primary"
              variant="flat"
              block
              @click="closeResetDialog"
            >
              Inapoi la Login
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import axios from "axios";
import {BASE_URL} from "@/composables/url_config";
import {log} from "@/composables/useLog";
import {sendSecurityCode} from "@/composables/useSendSecurityCode";
import {resetMyPassword} from "@/composables/useResetPassword";
import {sendUsernameReminder} from "@/composables/useSendUsernameReminder";
import {checkUsernameAvailability} from "@/composables/useCheckUsernameAvailability";
import {useMaintenanceCheck} from "@/composables/useMaintenanceCheck";
import AntibotVerification from "@/components/AntibotVerification.vue";

export default {
  components: { AntibotVerification },
  data() {
    return {
      username: "",
      password: "",
      showPassword: false,
      error: false,
      connectionError: false,
      valid: false,
      loading: false,
      showLoginUser: true,
      showRegisterNewUserLevel1: false,
      showRegisterNewUserLevel2: false,
      email: "",
      regUsername: "",
      regStep: 1,
      referral_code: "",
      registration_confirmation_code: "",
      checkingUsername: false,
      checkingEmail: false,
      usernameAvailable: null,
      usernameCheckTimer: null,
      emailExistsWarning: false,
      sendingUsernameReminder: false,
      usernameReminderSent: null,

      // T&C agreement
      agreeToTermsLogin: true,
      agreeToTermsRegister: false,
      antibotValid: false,

      // Reset password state
      showResetPasswordDialog: false,
      resetStep: 1,
      resetUsername: '',
      showForgotUsername: false,
      forgotEmail: '',
      resetCode: '',
      newPassword: '',
      showNewPassword: false,
      resetLoading: false,
      resetError: null,
      resetSuccess: null,
      retriesExceeded: false,
      failedAttempts: 0,

      // Validation rules
      inputRulesEmail: [
        v => !!v || 'Acest camp este obligatoriu',
        v => /.+@.+\..+/.test(v) || 'Adresa de email invalida'
      ],
      inputRulesConfirmationCode: [
        v => !!v || 'Acest camp este obligatoriu',
        v => String(v).length === 4 || 'Codul trebuie sa aiba 4 cifre'
      ],
      inputRulesUsername: [
        v => !!v || 'Acest camp este obligatoriu',
        v => v.length >= 5 || 'Minim 5 caractere'
      ],
      inputRulesPassword: [
        v => !!v || 'Acest camp este obligatoriu',
        v => v.length >= 6 || 'Minim 6 caractere'
      ],
      newPasswordRules: [
        v => !!v || 'Parola este obligatorie',
        v => v.length >= 6 || 'Minim 6 caractere',
        v => /^[a-zA-Z0-9]+$/.test(v) || 'Doar litere (a-z, A-Z) si cifre (0-9)'
      ]
    };
  },
  computed: {
    isValidNewPassword() {
      return this.newPassword &&
        this.newPassword.length >= 6 &&
        /^[a-zA-Z0-9]+$/.test(this.newPassword);
    },
    usernameAvailabilityMessage() {
      if (this.checkingUsername) return 'Se verifica...';
      if (this.usernameAvailable === true) return 'Username disponibil';
      if (this.usernameAvailable === false) return 'Username indisponibil';
      return '';
    }
  },
  watch: {
    regUsername(val) {
      this.usernameAvailable = null;
      if (this.usernameCheckTimer) clearTimeout(this.usernameCheckTimer);
      if (!val || val.length < 5) return;
      this.usernameCheckTimer = setTimeout(() => {
        this.checkUsername(val);
      }, 500);
    }
  },
  methods: {
    cancelError() {
      this.error = false;
    },
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    setShowLoginUser() {
      this.showLoginUser = true;
      this.showRegisterNewUserLevel1 = false;
      this.regStep = 1;
      this.emailExistsWarning = false;
      this.usernameReminderSent = null;
    },
    setShowRegisterNewUser() {
      this.showLoginUser = false;
      this.showRegisterNewUserLevel1 = true;
      this.regStep = 1;
      this.emailExistsWarning = false;
      this.usernameReminderSent = null;
    },

    async login() {
      this.loading = true;
      this.error = false;
      this.connectionError = false;

      const { checkApiReachable } = useMaintenanceCheck();
      const status = await checkApiReachable();
      if (!status.reachable) {
        this.connectionError = true;
        this.loading = false;
        return;
      }

      const url = BASE_URL + '/home/login';
      try {
        const response = await axios.post(url, {
          username: this.username,
          password: this.password,
        });
        if (response.data.error) {
          this.error = true;
        } else {
          log(response.data);
          this.$emit("goToLevel2", response.data);
        }
      } catch {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },

    async checkUsername(username) {
      this.checkingUsername = true;
      try {
        const result = await checkUsernameAvailability(username);
        if (this.regUsername === username) {
          this.usernameAvailable = result.available;
        }
      } catch {
        this.usernameAvailable = null;
      } finally {
        this.checkingUsername = false;
      }
    },

    async sendUsernameReminderFromReg() {
      this.sendingUsernameReminder = true;
      this.usernameReminderSent = null;
      try {
        const result = await sendUsernameReminder(this.email);
        if (result && result.error === false) {
          this.usernameReminderSent = result.message || 'Verifica email-ul pentru username-urile asociate.';
        } else {
          this.usernameReminderSent = result?.message || 'Eroare la trimitere.';
        }
      } catch {
        this.usernameReminderSent = 'Eroare la trimitere.';
      } finally {
        this.sendingUsernameReminder = false;
      }
    },

    async goToRegStep2() {
      if (!this.email || !/.+@.+\..+/.test(this.email)) return;
      // Daca deja a vazut warningul, trece direct la step 2
      if (this.emailExistsWarning) {
        this.regStep = 2;
        this.emailExistsWarning = false;
        this.usernameReminderSent = null;
        return;
      }
      this.checkingEmail = true;
      try {
        const response = await axios.post(BASE_URL + '/home/checkEmailExists', { email: this.email });
        const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
        if (data.exists) {
          this.emailExistsWarning = true;
        } else {
          this.regStep = 2;
        }
      } catch {
        this.regStep = 2;
      } finally {
        this.checkingEmail = false;
      }
    },

    async registerNewUser() {
      const url = BASE_URL + '/home/registerNewUserApi';
      this.loading = true;
      try {
        const antibotData = this.$refs.antibot ? this.$refs.antibot.getAntibotData() : {};
        const params = {
          email: this.email,
          password: this.password,
          username: this.regUsername,
          website: antibotData.website || '',
          elapsed_ms: antibotData.elapsed_ms || 0,
        };
        if (this.referral_code && this.referral_code.trim() !== '') {
          params.referral_code = this.referral_code.trim();
        }
        const response = await axios.post(url, params);
        if (response.data.error) {
          this.loading = false;
          alert(response.data.message);
        } else {
          log(response.data);
          this.showRegisterNewUserLevel1 = false;
          this.showRegisterNewUserLevel2 = true;
        }
      } catch {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },

    async checkConfirmationCode() {
      const url = BASE_URL + '/home/checkConfirmationCodeApi';
      this.loading = true;
      try {
        const response = await axios.post(url, {
          email: this.email,
          username: this.regUsername,
          confirmation_code: this.registration_confirmation_code,
          password: this.password,
        });
        if (response.data.error) {
          this.error = true;
        } else {
          log(response.data);
          this.$emit("goToDashboardNewUser", response.data);
        }
      } catch {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },

    // ===== Reset Password Methods =====
    closeResetDialog() {
      this.showResetPasswordDialog = false;
      this.resetStep = 1;
      this.resetUsername = '';
      this.resetCode = '';
      this.newPassword = '';
      this.resetError = null;
      this.resetSuccess = null;
      this.retriesExceeded = false;
      this.failedAttempts = 0;
      this.showForgotUsername = false;
      this.forgotEmail = '';
    },

    async sendForgotUsername() {
      this.resetLoading = true;
      this.resetError = null;
      this.resetSuccess = null;

      try {
        const result = await sendUsernameReminder(this.forgotEmail);
        log("sendForgotUsername result:", result);

        if (result && result.error === false) {
          this.resetSuccess = result.message || 'Verifica email-ul pentru username-urile asociate.';
        } else {
          this.resetError = result?.message || 'Eroare la trimiterea reminder-ului.';
        }
      } catch (err) {
        log("sendForgotUsername error:", err);
        this.resetError = 'Eroare la trimiterea reminder-ului.';
      } finally {
        this.resetLoading = false;
      }
    },

    async sendResetCode() {
      this.resetLoading = true;
      this.resetError = null;
      this.resetSuccess = null;

      try {
        const result = await sendSecurityCode(this.resetUsername);
        log("sendResetCode result:", result);

        if (result && result.error === false) {
          this.resetStep = 2;
          this.resetCode = '';
          this.newPassword = '';
          this.retriesExceeded = false;
          this.failedAttempts = 0;
        } else if (result && result.error === true) {
          // Parse JSON string if needed
          const data = typeof result === 'string' ? JSON.parse(result) : result;
          this.resetError = data.message || 'Eroare la trimiterea codului.';
        } else {
          this.resetError = 'Eroare la trimiterea codului.';
        }
      } catch (err) {
        log("sendResetCode error:", err);
        this.resetError = 'Eroare la trimiterea codului.';
      } finally {
        this.resetLoading = false;
      }
    },

    async resendResetCode() {
      this.resetLoading = true;
      this.resetError = null;

      try {
        const result = await sendSecurityCode(this.resetUsername);
        const data = typeof result === 'string' ? JSON.parse(result) : result;

        if (data && data.error === false) {
          this.resetCode = '';
          this.newPassword = '';
          this.retriesExceeded = false;
          this.failedAttempts = 0;
        } else {
          this.resetError = data?.message || 'Eroare la retrimiterea codului.';
        }
      } catch (err) {
        this.resetError = 'Eroare la retrimiterea codului.';
      } finally {
        this.resetLoading = false;
      }
    },

    async submitResetPassword() {
      this.resetLoading = true;
      this.resetError = null;

      try {
        const result = await resetMyPassword(this.resetUsername, this.resetCode, this.newPassword);
        const data = typeof result === 'string' ? JSON.parse(result) : result;

        if (data && data.error === false) {
          this.resetStep = 3;
        } else if (data) {
          this.resetError = data.message || 'Cod invalid.';
          this.failedAttempts++;

          if (data.blocked) {
            // Cont blocat - inchide dialogul
            this.retriesExceeded = true;
            this.resetCode = '';
          } else if (data.retries_exceeded || this.failedAttempts >= 3) {
            this.retriesExceeded = true;
            this.resetCode = '';
          }
        }
      } catch (err) {
        this.resetError = 'Eroare la resetarea parolei.';
      } finally {
        this.resetLoading = false;
      }
    }
  },
  created() {
    log("LoginLevel1 created");
    // Open the register flow directly when arriving from a "Create account" link
    if (this.$route && this.$route.query && this.$route.query.register) {
      this.setShowRegisterNewUser();
    }
  }
};
</script>
